import { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import Loading from "../../routes/Loading";
import CloseButton from "../buttons/CloseButton";
import TextButton from "../buttons/TextButton";
import {
  capitalizarTexto,
  formatearFechaInput,
} from "../../functions/dataFunctions";
import { eventos } from "./data/FormContent";
import FormComplete from "./components/FormComplete";
import FormGroups from "./components/FormGroups";
import FormSection from "./components/FormSection";
import { listarColeccion } from "../../services/collections.service";
import { cacheStore, refreshCache } from "../../services/cache";
import {
  validarFormulario,
  mapearDatosFormulario,
  guardarEvento,
} from "../../functions/eventFunctions";

import {
  stockOptions,
  personasOptions,
  empresasOptions,
  vehiculosOptions,
  proveedoresOptions,
  llavesOptions,
  porteriaOptions,
  satelitalOptions,
  tractoresOptions,
  furgonesOptions,
  sectoresOptions,
} from "./data/OptionsContent";

import "./css/Forms.css";

const FormEvents = ({
  elemento = {},
  title = "Formulario",
  subcoleccion,
  coleccion,
  filtro = null,
  subFiltro = null,
  onClose,
  onGuardar,
  campoNoEditable = null,
}) => {
  const modoEdicion = elemento?.id;

  const titulo = modoEdicion ? "Editar evento" : "Agregar evento";
  const subtitulo = title ? capitalizarTexto(title) : "Formulario";

  const campos = useMemo(
    () => eventos[subFiltro || filtro || coleccion] || [],
    /* `subFiltro` FALTABA en las dependencias. Antes daba igual porque nadie
     * se lo pasaba —llegaba siempre `undefined`—; ahora que la ficha lo manda,
     * sin él el memo se quedaría con los campos del formulario anterior. */
    [subFiltro, filtro, coleccion],
  );

  const [loading, setLoading] = useState(true);

  const [empresas, setEmpresas] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [mecanicos, setMecanicos] = useState([]);
  const [tractores, setTractores] = useState([]);
  const [furgones, setFurgones] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [serenos, setSerenos] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [stock, setStock] = useState([]);

  const [formData, setFormData] = useState(
    campos.reduce((acc, campo) => {
      if (campo.type === "group") {
        acc[campo.key] = campo.items.reduce((subAcc, item) => {
          subAcc[item.key] = elemento?.[campo.key]?.[item.key] ?? false;
          return subAcc;
        }, {});
      }

      if (campo.dato === "array") {
        acc[campo.key] = elemento?.[campo.key] ?? [];
      } else {
        acc[campo.key] = elemento?.[campo.key] ?? "";
      }

      return acc;
    }, {}),
  );

  useEffect(() => {
    if (!elemento?.id) return;

    const nuevosDatos = campos.reduce((acc, campo) => {
      if (campo.type === "group") {
        acc[campo.key] = campo.items.reduce((subAcc, item) => {
          subAcc[item.key] = elemento?.[campo.key]?.[item.key] ?? false;
          return subAcc;
        }, {});
        return acc;
      }

      let valor = elemento[campo.key] ?? "";

      if (campo.dato === "date" && valor) {
        valor = formatearFechaInput(valor);
      }

      acc[campo.key] = valor;
      return acc;
    }, {});

    setFormData(nuevosDatos);
    // Va por `elemento?.id` y no por `elemento` a propósito: el objeto llega
    // nuevo en cada render del padre, y reiniciar el formulario por eso
    // borraría lo que se está tipeando. Cambia la fila → se recarga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elemento?.id, campos]);

  // Carga de datos
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setSucursales(await listarColeccion("ubicaciones"));

        setEmpresas(cacheStore.empresas?.data || []);
        setProveedores(cacheStore.proveedores?.data || []);
        setStock(cacheStore.stock?.data || []);
        setPersonas(cacheStore.personas?.data || []);
        setMecanicos(cacheStore.mecanicos?.data || []);
        setChoferes(cacheStore.choferes?.data || []);
        setSerenos(cacheStore.serenos?.data || []);
        setTractores(cacheStore.tractores?.data || []);
        setFurgones(cacheStore.furgones?.data || []);
        setVehiculos(cacheStore.vehiculos?.data || []);
        setSectores(cacheStore.sectores?.data || []);
      } catch (error) {
        console.error("[Error] cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, []);

  // Opciones para selects
  const optionsMap = useMemo(
    () => ({
      empresas: empresasOptions(empresas),
      personas: personasOptions(personas),
      mecanicos: personasOptions(mecanicos),
      serenos: personasOptions(serenos),
      choferes: personasOptions(choferes),
      sucursales: empresasOptions(sucursales),
      tractores: vehiculosOptions(tractores),
      furgones: vehiculosOptions(furgones),
      vehiculos: vehiculosOptions(vehiculos),
      proveedores: proveedoresOptions(proveedores),
      stock: stockOptions(stock),
      sectores: sectoresOptions(sectores),

      llaves: llavesOptions(), // se llama directamente desde tiposPorteria / tiposTractores
      tiposPorteria: porteriaOptions(filtro),
      tiposSatelital: satelitalOptions(),
      tiposTractores: tractoresOptions(),
      tiposFurgones: furgonesOptions(),
    }),
    [
      empresas,
      personas,
      mecanicos,
      serenos,
      choferes,
      sucursales,
      tractores,
      furgones,
      vehiculos,
      proveedores,
      stock,
      sectores,
      filtro,
    ],
  );

  const cargarOpciones = (col) => optionsMap[col] || [];

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario(formData, campos)) return;

    setLoading(true);

    try {
      const data = mapearDatosFormulario(formData, campos);

      await guardarEvento(
        elemento,
        { ...data, area: coleccion },
        formData,
        campos,
        coleccion,
        modoEdicion,
      );
    } catch (error) {
      console.error("[Error] al guardar", error);

      Swal.fire({
        title: "Error",
        text: error?.message || "No hemos podido procesar la solicitud.",
        icon: "error",
        confirmButtonColor: "var(--acc, #4161bd)",
      });
    } finally {
      if (onGuardar) await onGuardar();
      refreshCache("eventos");
      onClose();
      setLoading(false);
    }
  };

  const bloquePrincipal = campos.filter((cp) => cp.type === "principal");
  const bloqueSecondary = campos.filter((cp) => cp.type === "secondary");
  const bloqueGrupos = campos.filter((cp) => cp.type === "group");
  const bloqueComplete = campos.filter((cp) => cp.type === "groupComplete");

  return (
    <div className="doble-form">
      {loading ? (
        <Loading />
      ) : (
        <div className="doble-form-content">
          <CloseButton onClose={onClose} />

          <h1 className="form-header">
            <span className="form-titlebottom">{titulo}</span>
            <strong className="form-title">{subtitulo}</strong>
          </h1>

          <hr />

          {/* SIN CAMPOS NO SE DIBUJA UN FORMULARIO VACÍO, se dice por qué.
              Los campos salen de `eventos[área]` y hay áreas que no tienen
              ninguno cargado —`satelital`, `administracion`, las 57 filas sin
              área—. Un modal en blanco con un botón de guardar se lee como
              que la pantalla se rompió, y el que lo ve reporta un bug que no
              existe: lo que falta es la definición del formulario. */}
          {campos.length === 0 ? (
            <div className="doble-form-modal">
              <p className="tenue">
                Este evento es del área{" "}
                <b>{elemento?.area || "sin área cargada"}</b> y todavía no tiene
                formulario definido, así que no se puede editar desde acá. Los
                eventos de portería, tractores, furgones y tráfico sí.
              </p>
            </div>
          ) : (
            <div className="doble-form-modal">
              <div className="doble-form-left">
                {bloquePrincipal.length > 0 && (
                  <FormSection
                    title="Información"
                    campos={bloquePrincipal}
                    formData={formData}
                    handleChange={handleChange}
                    cargarOpciones={cargarOpciones}
                    modoEdicion={modoEdicion}
                    campoNoEditable={campoNoEditable}
                  />
                )}
              </div>

              <div className="doble-form-right">
                {bloqueSecondary.length > 0 && (
                  <FormSection
                    title="Otros datos"
                    campos={bloqueSecondary}
                    formData={formData}
                    handleChange={handleChange}
                    cargarOpciones={cargarOpciones}
                    modoEdicion={modoEdicion}
                    campoNoEditable={campoNoEditable}
                  />
                )}

                {bloqueGrupos.length > 0 && (
                  <FormGroups
                    grupos={bloqueGrupos}
                    formData={formData}
                    setFormData={setFormData}
                  />
                )}
                {bloqueComplete.length > 0 && (
                  <FormComplete
                    grupos={bloqueComplete}
                    formData={formData}
                    setFormData={setFormData}
                    handleChange={handleChange}
                    cargarOpciones={cargarOpciones}
                    coleccion={coleccion}
                  />
                )}
              </div>
            </div>
          )}

          {/* Sin campos tampoco va el botón de guardar: guardar un formulario
              vacío pisaría la fila con nada. */}
          {campos.length > 0 && (
            <div className="form-buttons">
              <TextButton text="Guardar" type="button" onClick={handleSubmit} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FormEvents;
